import Button, { IconButton } from "@/lib/components/ui/button/button";
import { FieldError } from "@/lib/components/ui/field/field";
import X from "@/lib/components/ui/icons/x";
import Input from "@/lib/components/ui/input/input";
import Spinner from "@/lib/components/ui/spinner";
import Stepper from "@/lib/components/ui/stepper/stepper";
import Text from "@/lib/components/ui/text/text";
import Textarea from "@/lib/components/ui/textarea/textarea";
import {
  RadioRow,
  RowText,
} from "@/lib/components/ui/settings-rows/settings-rows";
import {
  BrandingPreview,
  ColorPicker,
  LogoImage,
  toHex,
  useLogoFileRules,
} from "@/modules/tenants/components/tenant-branding";
import { formatFileSize, log } from "@soulbyte/core";
import { ArrowLeft, ArrowRight, Check, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  patchTenantAfterBrandingStepComplete,
  useDeleteTenantLogo,
  useTenantSetupWizardEligibility,
  useUpdateTenant,
  useUploadTenantLogo,
  type TenantWizardViewModel,
} from "@/modules/tenants/tenants.api";
import { useTenantsContext } from "@/modules/tenants/tenants.provider";
import {
  TENANT_SETUP_BRAND_COLOR_CHOICES,
  TENANT_SETUP_SIDEBAR_COLOR_CHOICES,
} from "./tenant-setup-dialog.color-presets";
import {
  TENANT_SETUP_INDUSTRY_ICON_BY_ID,
  TENANT_SETUP_INDUSTRY_IDS,
  TENANT_SETUP_INDUSTRY_LABEL_KEY_BY_ID,
} from "./tenant-setup-dialog.industry-options";
import { useTenantSetupDialogLifecycleContext } from "./tenant-setup-dialog.lifecycle";
import { patchTenantAfterDescriptionStep } from "./tenant-setup-dialog.patch";

type StepId = "logo" | "company_name" | "industry" | "description" | "colors";

type Draft = {
  company_name: string;
  industry: string;
  description: string;
  sidebar: string;
  brand: string;
};

const TEXT_LIMITS = { company_name: 200, description: 4000 };

/** TenantSetupWizard is the company setup, one step at a time. */
export function TenantSetupWizard() {
  const { t } = useTranslation();
  const { closeDialog } = useTenantSetupDialogLifecycleContext();
  const { wizardViewModel } = useTenantsContext();
  const { canUploadTenantLogo } = useTenantSetupWizardEligibility();
  const { isPending: isSaving, updateTenant } = useUpdateTenant();
  const { isPending: isUploading, uploadTenantLogo } = useUploadTenantLogo();
  const { isPending: isDeleting, deleteTenantLogo } = useDeleteTenantLogo();

  const hasBranding = wizardViewModel?.plan_key === "enterprise";
  const steps: StepId[] = [
    ...(hasBranding && canUploadTenantLogo ? (["logo"] as const) : []),
    "company_name",
    "industry",
    "description",
    ...(hasBranding ? (["colors"] as const) : []),
  ];

  const [index, setIndex] = useState(0);
  const [draft, setDraft] = useState(() => startDraft(wizardViewModel));
  const [logo, setLogo] = useState<LogoChange>(null);
  const [showError, setShowError] = useState(false);

  // a blob url keeps the file in memory, so release it once the logo is
  // replaced, uploaded or the setup closes
  const pickedLogoUrl = logo && logo !== "remove" ? logo.url : undefined;
  useEffect(
    function releasePickedLogo() {
      return () => {
        if (pickedLogoUrl) URL.revokeObjectURL(pickedLogoUrl);
      };
    },
    [pickedLogoUrl],
  );

  const current = steps[index];
  const isLast = index === steps.length - 1;
  const isBusy = isSaving || isUploading || isDeleting;
  const error = errorFor(current);
  const shownError = showError ? error : null;

  function errorFor(step: StepId) {
    if (step === "company_name") {
      if (!draft.company_name.trim())
        return t("company_setup_validation_company_name_required");
      if (draft.company_name.trim().length > TEXT_LIMITS.company_name)
        return t("company_setup_validation_company_name_max", {
          max: TEXT_LIMITS.company_name,
        });
    }
    if (step === "industry" && !draft.industry) {
      return t("company_setup_validation_industry_invalid");
    }
    if (step === "description") {
      if (!draft.description.trim())
        return t("company_setup_validation_description_required");
      if (draft.description.trim().length > TEXT_LIMITS.description)
        return t("company_setup_validation_description_max", {
          max: TEXT_LIMITS.description,
        });
    }
    return null;
  }

  function edit(patch: Partial<Draft>) {
    setDraft((prev) => ({ ...prev, ...patch }));
  }

  function goTo(next: number) {
    setShowError(false);
    setIndex(next);
  }

  // every step saves its own piece, so closing halfway keeps what is done
  async function saveStep(step: StepId) {
    if (step === "logo") {
      if (logo === "remove") await deleteTenantLogo();
      else if (logo) await uploadTenantLogo(logo.file);
      setLogo(null);
      return;
    }
    if (step === "company_name") {
      await updateTenant({ company_name: draft.company_name.trim() });
    }
    if (step === "industry") await updateTenant({ industry: draft.industry });
    if (step === "description") {
      await updateTenant(
        patchTenantAfterDescriptionStep({
          description: draft.description.trim(),
          branding_step_enabled: hasBranding,
          current_company_onboarding: wizardViewModel?.company_onboarding,
        }),
      );
    }
    if (step === "colors") {
      await updateTenant(
        patchTenantAfterBrandingStepComplete({
          sidebar_color: draft.sidebar,
          brand_color: draft.brand,
        }),
      );
    }
  }

  async function goNext() {
    if (error) {
      setShowError(true);
      return;
    }
    try {
      await saveStep(current);
    } catch (error) {
      // the failed save already shows a toast, sentry still needs the step
      log.error(error, {
        tags: { error_type: "company_setup_wizard", step: current },
      });
      return;
    }
    if (!isLast) {
      goTo(index + 1);
      return;
    }
    closeDialog();
    toast.success(t("company_setup_done"));
  }

  function goBack() {
    if (index === 0) closeDialog();
    else goTo(index - 1);
  }

  const savedLogoUrl = wizardViewModel?.logo_url?.trim();
  const logoUrl = logo === "remove" ? undefined : (logo?.url ?? savedLogoUrl);
  const logoImage = (
    <LogoImage url={logoUrl} name={draft.company_name} className="size-full" />
  );

  return (
    <form
      className="flex h-full flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        void goNext();
      }}
    >
      <header className="flex items-center gap-4 px-5 pt-5 pb-3">
        <div className="flex flex-1 flex-col gap-1">
          <Text as="h2" variant="title" size="sm">
            {t("company_setup_dialog_title")}
          </Text>
          <Text className="text-foreground-pressed">
            {t("company_setup_dialog_step_progress", {
              current: index + 1,
              total: steps.length,
            })}
          </Text>
        </div>
        <Stepper
          aria-label={t("company_setup_dialog_step_progress", {
            current: index + 1,
            total: steps.length,
          })}
          currentStep={index + 1}
          totalSteps={steps.length}
        />
        <IconButton
          aria-label={t("company_setup_dialog_close_aria")}
          size="sm"
          variant="ghost"
          type="button"
          onClick={closeDialog}
        >
          <X />
        </IconButton>
      </header>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 pb-5">
        {current === "logo" ? (
          <StepRow
            title={t("company_setup_section_logo_title")}
            description={t("company_setup_logo_hint")}
          >
            <LogoEditor
              url={logoUrl}
              name={draft.company_name}
              disabled={isBusy}
              onPick={(file, url) => setLogo({ file, url })}
              onRemove={() => setLogo(savedLogoUrl ? "remove" : null)}
            />
          </StepRow>
        ) : null}
        {current === "company_name" ? (
          <StepRow
            title={t("company_setup_field_company_name_label")}
            description={t("company_setup_step_name_description")}
            error={shownError}
          >
            <Input
              autoFocus
              aria-label={t("company_setup_field_company_name_label")}
              autoComplete="organization"
              placeholder={t("company_setup_field_company_name_placeholder")}
              value={draft.company_name}
              onChange={(event) => edit({ company_name: event.target.value })}
            />
          </StepRow>
        ) : null}
        {current === "industry" ? (
          <StepRow
            title={t("company_setup_field_industry_label")}
            description={t("company_setup_step_industry_description")}
            error={shownError}
          >
            <div
              role="radiogroup"
              aria-label={t("company_setup_field_industry_label")}
              className="border-border grid grid-cols-2 overflow-clip rounded-lg border"
            >
              {TENANT_SETUP_INDUSTRY_IDS.map((id) => {
                const Icon = TENANT_SETUP_INDUSTRY_ICON_BY_ID[id];
                return (
                  <RadioRow
                    key={id}
                    icon={<Icon />}
                    title={t(TENANT_SETUP_INDUSTRY_LABEL_KEY_BY_ID[id])}
                    checked={draft.industry === id}
                    onSelect={() => edit({ industry: id })}
                  />
                );
              })}
            </div>
          </StepRow>
        ) : null}
        {current === "description" ? (
          <StepRow
            title={t("settings_branding_description")}
            description={t("company_setup_step_description_description")}
            error={shownError}
          >
            <Textarea
              autoFocus
              aria-label={t("settings_branding_description")}
              placeholder={t("company_setup_field_description_placeholder")}
              rows={4}
              value={draft.description}
              onChange={(event) => edit({ description: event.target.value })}
            />
          </StepRow>
        ) : null}
        {current === "colors" ? (
          <div className="flex flex-col gap-5">
            <div className="grid gap-4 sm:grid-cols-2">
              {(["sidebar", "brand"] as const).map((part) => {
                const title = t(
                  part === "sidebar"
                    ? "company_setup_section_sidebar_color_title"
                    : "company_setup_section_brand_color_title",
                );
                return (
                  <div key={part} className="flex flex-col gap-2">
                    <RowText
                      title={title}
                      description={t(
                        part === "sidebar"
                          ? "settings_branding_sidebar_hint"
                          : "settings_branding_brand_hint",
                      )}
                    />
                    <ColorPicker
                      label={title}
                      choices={
                        part === "sidebar"
                          ? TENANT_SETUP_SIDEBAR_COLOR_CHOICES
                          : TENANT_SETUP_BRAND_COLOR_CHOICES
                      }
                      value={draft[part]}
                      onChange={(hex) => edit({ [part]: hex })}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex flex-col gap-2">
              <RowText title={t("settings_branding_preview")} />
              <BrandingPreview
                sidebar={draft.sidebar}
                brand={draft.brand}
                logo={logoImage}
              />
            </div>
          </div>
        ) : null}
      </div>

      <footer className="border-border flex items-center justify-between gap-2 border-t px-5 py-3">
        <Button type="button" variant="ghost" size="sm" onClick={goBack}>
          {index === 0 ? (
            t("common_cancel")
          ) : (
            <>
              <ArrowLeft />
              {t("company_setup_dialog_back")}
            </>
          )}
        </Button>
        <Button type="submit" size="sm" disabled={isBusy}>
          {isBusy ? <Spinner /> : null}
          {isLast
            ? t("company_setup_dialog_done")
            : t("company_setup_dialog_continue")}
          {isLast ? <Check /> : <ArrowRight />}
        </Button>
      </footer>
    </form>
  );
}

type LogoChange = { file: File; url: string } | "remove" | null;

function StepRow({
  title,
  description,
  error,
  children,
}: {
  title: string;
  description: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3">
      <RowText title={title} description={description} />
      {children}
      {error ? <FieldError>{error}</FieldError> : null}
    </div>
  );
}

// the logo picked here waits for the next step, so going back uploads nothing
function LogoEditor({
  url,
  name,
  disabled,
  onPick,
  onRemove,
}: {
  url: string | undefined;
  name: string;
  disabled: boolean;
  onPick: (file: File, url: string) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const { accept, maxBytes, checkLogoOrWarn } = useLogoFileRules();
  const fileInput = useRef<HTMLInputElement>(null);

  function pick(file: File | undefined) {
    if (!file || !checkLogoOrWarn(file)) return;
    onPick(file, URL.createObjectURL(file));
  }

  return (
    <div className="flex flex-wrap items-center gap-4">
      <LogoImage url={url} name={name} className="size-16" />
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled}
            onClick={() => fileInput.current?.click()}
          >
            <Upload />
            {t("settings_branding_logo_pick")}
          </Button>
          {url ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={onRemove}
            >
              <Trash2 />
              {t("company_setup_logo_remove")}
            </Button>
          ) : null}
        </div>
        <Text className="text-foreground-pressed">
          {t("company_setup_logo_upload_description", {
            maxSize: formatFileSize(maxBytes),
          })}
        </Text>
      </div>
      <input
        ref={fileInput}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(event) => {
          pick(event.target.files?.[0]);
          event.target.value = "";
        }}
      />
    </div>
  );
}

function startDraft(model: TenantWizardViewModel | undefined): Draft {
  return {
    company_name: model?.company_name ?? "",
    industry: model?.industry ?? "",
    description: model?.description ?? "",
    sidebar: toHex(model?.sidebar_color, TENANT_SETUP_SIDEBAR_COLOR_CHOICES[0]),
    brand: toHex(model?.brand_color, TENANT_SETUP_BRAND_COLOR_CHOICES[0]),
  };
}
